import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "verification-recorder__preview" }
const _hoisted_2 = { class: "verification-recorder__overlay" }
const _hoisted_3 = { class: "verification-recorder__toolbar" }
const _hoisted_4 = {
  key: 1,
  class: "verification-recorder__toolbar-spacer"
}
const _hoisted_5 = { class: "verification-recorder__prompts" }
const _hoisted_6 = { class: "verification-recorder__phrase" }
const _hoisted_7 = { class: "verification-recorder__phrase-value" }
const _hoisted_8 = {
  key: 0,
  class: "verification-recorder__notice"
}
const _hoisted_9 = {
  key: 1,
  class: "verification-recorder__timer"
}
const _hoisted_10 = { class: "verification-recorder__timer-text" }
const _hoisted_11 = { class: "verification-recorder__controls" }
const _hoisted_12 = ["disabled"]
const _hoisted_13 = ["disabled"]
const _hoisted_14 = { class: "verification-recorder__hint" }
const _hoisted_15 = { key: 0 }
const _hoisted_16 = { key: 1 }
const _hoisted_17 = { key: 2 }

import {computed, onBeforeUnmount, onMounted, ref} from 'vue';
import {
  useVerificationVideoRecorder,
  VERIFICATION_PHRASE,
} from '@/composables/useVerificationVideoRecorder';

const maxDurationSeconds = 60;


export default /*@__PURE__*/_defineComponent({
  __name: 'VerificationRecorderView',
  props: {
    coupleAccount: { type: Boolean },
    phrase: {}
  },
  emits: ["cancel", "recorded", "error"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

const recorder = useVerificationVideoRecorder();
const previewVideo = ref<HTMLVideoElement | null>(null);
const initializing = ref(true);
const busy = ref(false);
const isRecording = ref(false);
const durationSeconds = ref(0);

let timerId: ReturnType<typeof setInterval> | null = null;
const phrase = computed(() => props.phrase || VERIFICATION_PHRASE);

const formattedDuration = computed(() => {
  const minutes = Math.floor(durationSeconds.value / 60).toString().padStart(2, '0');
  const seconds = (durationSeconds.value % 60).toString().padStart(2, '0');
  return `${minutes}:${seconds}`;
});

const clearTimer = () => {
  if (timerId) {
    clearInterval(timerId);
    timerId = null;
  }
};

const setupPreview = async () => {
  initializing.value = true;

  try {
    await recorder.initializePreview();
    if (previewVideo.value) {
      await recorder.attachToVideoElement(previewVideo.value);
    }
  } catch {
    emit('error', 'Camera and microphone access is required to record your verification video.');
  } finally {
    initializing.value = false;
  }
};

const startTimer = () => {
  clearTimer();
  durationSeconds.value = 0;
  timerId = setInterval(() => {
    durationSeconds.value += 1;
    if (durationSeconds.value >= maxDurationSeconds) {
      stopRecording();
    }
  }, 1000);
};

const startRecording = async () => {
  if (initializing.value || busy.value || isRecording.value) {
    return;
  }

  try {
    busy.value = true;
    await recorder.startRecording();
    isRecording.value = true;
    startTimer();
  } catch {
    emit('error', 'Unable to start recording. Please try again.');
  } finally {
    busy.value = false;
  }
};

const stopRecording = async () => {
  if (!isRecording.value || busy.value) {
    return;
  }

  try {
    busy.value = true;
    clearTimer();
    const blob = await recorder.stopRecording();
    isRecording.value = false;
    emit('recorded', blob);
  } catch {
    emit('error', 'Unable to finish recording. Please try again.');
  } finally {
    busy.value = false;
  }
};

const flipCamera = async () => {
  if (isRecording.value || initializing.value) {
    return;
  }

  try {
    await recorder.flipCamera();
    if (previewVideo.value) {
      await recorder.attachToVideoElement(previewVideo.value);
    }
  } catch {
    emit('error', 'Unable to switch camera.');
  }
};

onMounted(setupPreview);

onBeforeUnmount(async () => {
  clearTimer();
  await recorder.destroy();
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(["verification-recorder", { 'verification-recorder--native': _unref(recorder).isNative }])
  }, [
    _createElementVNode("div", _hoisted_1, [
      (!_unref(recorder).isNative)
        ? (_openBlock(), _createElementBlock("video", {
            key: 0,
            ref_key: "previewVideo",
            ref: previewVideo,
            class: "verification-recorder__video",
            playsinline: "",
            muted: ""
          }, null, 512))
        : _createCommentVNode("", true),
      _createElementVNode("div", _hoisted_2, [
        _createElementVNode("div", _hoisted_3, [
          (!isRecording.value)
            ? (_openBlock(), _createElementBlock("button", {
                key: 0,
                type: "button",
                onClick: _cache[0] || (_cache[0] = ($event: any) => (emit('cancel'))),
                class: "verification-recorder__chip"
              }, " Back "))
            : (_openBlock(), _createElementBlock("div", _hoisted_4)),
          (!isRecording.value)
            ? (_openBlock(), _createElementBlock("button", {
                key: 2,
                type: "button",
                onClick: flipCamera,
                class: "verification-recorder__chip"
              }, " Flip "))
            : _createCommentVNode("", true)
        ]),
        _createElementVNode("div", _hoisted_5, [
          _createElementVNode("div", _hoisted_6, [
            _cache[1] || (_cache[1] = _createElementVNode("div", { class: "verification-recorder__phrase-label" }, "Say this phrase clearly", -1)),
            _createElementVNode("div", _hoisted_7, "\"" + _toDisplayString(phrase.value) + "\"", 1)
          ]),
          (__props.coupleAccount)
            ? (_openBlock(), _createElementBlock("div", _hoisted_8, " Both of you must be visible in the video. "))
            : _createCommentVNode("", true)
        ])
      ]),
      (isRecording.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
            _cache[2] || (_cache[2] = _createElementVNode("span", { class: "verification-recorder__timer-dot" }, null, -1)),
            _createElementVNode("span", _hoisted_10, _toDisplayString(formattedDuration.value), 1)
          ]))
        : _createCommentVNode("", true)
    ]),
    _createElementVNode("div", _hoisted_11, [
      (!isRecording.value)
        ? (_openBlock(), _createElementBlock("button", {
            key: 0,
            type: "button",
            onClick: startRecording,
            disabled: initializing.value || busy.value,
            class: "verification-recorder__record-btn verification-recorder__record-btn--start"
          }, [...(_cache[3] || (_cache[3] = [
            _createElementVNode("span", { class: "verification-recorder__record-btn-inner" }, null, -1)
          ]))], 8, _hoisted_12))
        : (_openBlock(), _createElementBlock("button", {
            key: 1,
            type: "button",
            onClick: stopRecording,
            disabled: busy.value || durationSeconds.value < 2,
            class: "verification-recorder__record-btn verification-recorder__record-btn--stop"
          }, [...(_cache[4] || (_cache[4] = [
            _createElementVNode("span", { class: "verification-recorder__stop-icon" }, null, -1)
          ]))], 8, _hoisted_13)),
      _createElementVNode("p", _hoisted_14, [
        (!isRecording.value)
          ? (_openBlock(), _createElementBlock("span", _hoisted_15, "Tap the button when you're ready to record."))
          : (durationSeconds.value < 2)
            ? (_openBlock(), _createElementBlock("span", _hoisted_16, "Keep recording…"))
            : (_openBlock(), _createElementBlock("span", _hoisted_17, "Tap the square to finish recording."))
      ])
    ])
  ], 2))
}
}

})